import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import ScheduleGrid, { colorFor } from "@/components/ScheduleGrid";
import EventModal from "@/components/EventModal";
import { isVirtualEvent } from "@/lib/events";
import Seo, { stripHtml } from "@/components/Seo";

const eventJsonLd = (e) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  name: e.title,
  startDate: e.date && e.start_time ? `${e.date}T${e.start_time}` : e.date || undefined,
  endDate: e.date && e.end_time ? `${e.date}T${e.end_time}` : undefined,
  description: stripHtml(e.description, 300) || undefined,
  image: e.image || undefined,
  eventAttendanceMode: isVirtualEvent(e)
    ? "https://schema.org/OnlineEventAttendanceMode"
    : "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: e.cancelled ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled",
  location: isVirtualEvent(e)
    ? { "@type": "VirtualLocation", url: `${window.location.origin}/events/${e.slug || e.id}` }
    : { "@type": "Place", name: e.location || "Enhancing Your World" },
  organizer: { "@type": "Organization", name: "Enhancing Your World", url: window.location.origin },
  offers: {
    "@type": "Offer",
    priceCurrency: "USD",
    price: (e.tiers || []).length ? Math.min(...e.tiers.map((t) => Number(t.price) || 0)) : Number(e.price || 0),
    url: `${window.location.origin}/events/${e.slug || e.id}`,
    availability: "https://schema.org/InStock",
  },
  performer: e.facilitator ? { "@type": "Person", name: e.facilitator } : undefined,
});

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full px-4 py-2 text-sm transition-colors ${active ? "bg-[#144F75] text-white" : "bg-white border border-[#144F75]/10 text-[#4A5D6B] hover:border-[#144F75]/40"}`}>
    {children}
  </button>
);

export default function SchedulePage() {
  const [selected, setSelected] = useState(null);
  const { id: routeId } = useParams();
  const nav = useNavigate();
  const [loc, setLoc] = useState("all");
  const [fac, setFac] = useState("all");
  const [type, setType] = useState("all");
  const [format, setFormat] = useState("all");

  const { data: full = [], isLoading } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data), refetchInterval: 60000 });
  const { data: disc } = useQuery({ queryKey: ["discovery-events"], queryFn: () => api.get("/discovery/events").then((r) => r.data), refetchInterval: 60000 });
  const facets = disc?.facets || { locations: [], facilitators: [], types: [] };

  // Enrich full events with their center for badge + filtering.
  const centerById = useMemo(() => Object.fromEntries((disc?.events || []).map((e) => [e.id, e.center])), [disc]);
  const enriched = useMemo(() => full.map((e) => ({ ...e, center: centerById[e.id] || null })), [full, centerById]);

  // Deep link: /events/:id-or-slug auto-opens the event.
  useEffect(() => {
    if (routeId && enriched.length) {
      const ev = enriched.find((x) => x.id === routeId || (x.slug && x.slug === routeId));
      if (ev) setSelected(ev);
    }
  }, [routeId, enriched]);

  // Keep the open event in sync with live data (capacity, schedule, status).
  const live = selected ? enriched.find((x) => x.id === selected.id) || selected : null;
  const closeModal = () => { setSelected(null); if (routeId) nav("/events"); };

  const filtered = useMemo(() => enriched.filter((e) =>
    (loc === "all" || e.center?.id === loc || e.location_id === "all") &&
    (fac === "all" || (e.facilitator || e.enhancer_name) === fac) &&
    (type === "all" || e.category === type) &&
    (format === "all" || (format === "online" ? isVirtualEvent(e) : !isVirtualEvent(e)))
  ), [enriched, loc, fac, type, format]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="events-page">
      <p className="eyw-label mb-3">Events & Retreats</p>
      <h1 className="font-serif text-4xl lg:text-6xl text-[#1A242B] mb-4 tracking-tight">Event and Retreat Calendar</h1>
      <p className="text-[#4A5D6B] max-w-2xl mb-8">Circles, retreats, workshops, and gatherings across our flagship centers and affiliate partners. Tap any event to register.</p>

      {/* Filters */}
      <div className="space-y-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2">Format</p>
          <div className="flex flex-wrap gap-2" data-testid="events-format-filters">
            <Chip active={format === "all"} onClick={() => setFormat("all")} testid="events-format-all">All formats</Chip>
            <Chip active={format === "in-person"} onClick={() => setFormat("in-person")} testid="events-format-in-person">In-person</Chip>
            <Chip active={format === "online"} onClick={() => setFormat("online")} testid="events-format-online">Online / virtual</Chip>
          </div>
        </div>
        {facets.locations.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2">Location</p>
            <div className="flex flex-wrap gap-2" data-testid="events-loc-filters">
              <Chip active={loc === "all"} onClick={() => setLoc("all")} testid="events-loc-all">All locations</Chip>
              {facets.locations.map((l) => (
                <Chip key={l.id} active={loc === l.id} onClick={() => setLoc(l.id)} testid={`events-loc-${l.slug || l.id}`}>{l.name}</Chip>
              ))}
            </div>
          </div>
        )}
        {facets.types.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2">Type</p>
            <div className="flex flex-wrap gap-2" data-testid="events-type-filters">
              <Chip active={type === "all"} onClick={() => setType("all")} testid="events-type-all">All types</Chip>
              {facets.types.map((t) => (
                <Chip key={t} active={type === t} onClick={() => setType(t)} testid={`events-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</Chip>
              ))}
            </div>
          </div>
        )}
        {facets.facilitators.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2">Facilitator</p>
            <div className="flex flex-wrap gap-2" data-testid="events-fac-filters">
              <Chip active={fac === "all"} onClick={() => setFac("all")} testid="events-fac-all">All facilitators</Chip>
              {facets.facilitators.map((f) => (
                <Chip key={f} active={fac === f} onClick={() => setFac(f)} testid={`events-fac-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{f}</Chip>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-8">
        {facets.types.map((c) => (
          <span key={c} className="inline-flex items-center gap-2 text-sm text-[#4A5D6B]">
            <span className="h-3 w-3 rounded-full" style={{ background: colorFor(c) }} /> {c}
          </span>
        ))}
      </div>

      {isLoading ? <p className="text-[#4A5D6B]">Loading schedule…</p>
        : filtered.length === 0 ? <p className="text-[#4A5D6B]" data-testid="events-empty">No events match these filters.</p>
        : <ScheduleGrid events={filtered} onEventClick={setSelected} />}

      {selected && !selected.is_placeholder ? (
        <Seo title={selected.title} description={stripHtml(selected.description) || `${selected.category || "Event"} on ${selected.date} at Enhancing Your World.`}
          image={selected.image} path={`/events/${selected.slug || selected.id}`} type="article" jsonLd={eventJsonLd(selected)} />
      ) : (
        <Seo title="Events & Retreats" description="Circles, retreats, workshops and gatherings across the Enhancing Your World centers. Browse the calendar and register." path="/events" />
      )}
      {live && <EventModal event={live} onClose={closeModal} />}
    </div>
  );
}
